<script setup lang="ts">
import PinDialog from '@/components/PinDialog.vue';
import PinForm from '@/components/PinForm.vue';
import router from '@/router';
import { useUserStore } from '@/stores/user';
import { useAnalyticsService } from '@/lib/util/analytics';
import { onMounted } from 'vue';
import { resetAnalyticsService } from '@/lib/util/analytics/resetAnalyticsService';
import { useEmployeeStore } from '@/stores/employees';
import { reloadAnalyticsService } from '@/lib/util/analytics/reloadAnalyticsService';

interface EventDetails {
  type?: string;
  error?: { message: string };
}

const props = defineProps<{ employeeId: number }>();

const user = useUserStore();

const EVENT_SOURCES = {
  PinScreen: 'PIN screen',
};

const EVENT_LOGIN_TYPE = {
  Pin: 'PIN',
  FaceUnlock: 'FACE_UNLOCK',
};

const login = (pin: string) => {
  trackLogin('LOGIN_REQUESTED', EVENT_LOGIN_TYPE.Pin);
  if (user.login(props.employeeId, pin)) {
    trackLogin('LOGIN_SUCCESS_ACKNOWLEDGED', EVENT_LOGIN_TYPE.Pin);
    router.push({ name: 'me' });
  } else {
    trackLogin(
      'LOGIN_FAILURE_ACKNOWLEDGED',
      EVENT_LOGIN_TYPE.Pin,
      user.errors?.authError?.message
    );
  }
};

function goHome() {
  resetAnalyticsService();
  router.push({ name: 'home' });
}

function trackLogin(eventName: string, type?: string, errorMessage?: string) {
  const latestAnalyticsService = useAnalyticsService();
  const details: EventDetails = {};

  if (type) {
    details.type = type;
  }

  if (errorMessage) {
    details.error = { message: errorMessage };
  }

  latestAnalyticsService.track(
    { name: eventName, screen: 'LOGIN' },
    Object.keys(details).length
      ? { source: EVENT_SOURCES.PinScreen, login: { details } }
      : { source: EVENT_SOURCES.PinScreen }
  );
}

onMounted(async () => {
  const employeeStore = useEmployeeStore();
  const employee = employeeStore.getByEmployeeId(props.employeeId);
  reloadAnalyticsService({
    employee,
  });
  trackLogin('LOGIN_STARTED');
});
</script>

<template>
  <PinDialog show-close @closed="goHome">
    <PinForm @login="login" :employee-id="employeeId" />
  </PinDialog>
</template>
